// src/client/assets/javascripts/components/SensorsTabs/Pressure/TrendOverlayGraph.js
// Multi-sensor overlay chart: one Plotly plot with rangeslider overview strip,
// unified hover readout, zoom-history toolbar, and right-click menu hook.

import React from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { connect } from 'react-redux';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { buildOverlayTraces, relayoutToRange, toPlotTime } from './overlayTraceUtils';

const PRESSURE_CONVERSION = {
  meter: { factor: 1, label: 'Meter' },
  bar: { factor: 0.09804139432, label: 'Bar' },
  kpa: { factor: 9.80665, label: 'kPa' },
  psi: { factor: 1.42233, label: 'PSI' },
};

// Millisecond-capable tick labels when zoomed into a transient (same stops as
// the transient waveform modal in PressureSensorTransientTab).
const TICKFORMAT_STOPS = [
  { dtickrange: [null, 1000], value: '%H:%M:%S.%L' },
  { dtickrange: [1000, 60000], value: '%H:%M:%S' },
  { dtickrange: [60000, 3600000], value: '%H:%M' },
  { dtickrange: [3600000, 86400000], value: '%H:%M\n%b %d' },
  { dtickrange: [86400000, null], value: '%b %d' },
];

const CONFIG = {
  displaylogo: false,
  responsive: true,
  displayModeBar: true,
  modeBarButtonsToRemove: [
    'select2d',
    'lasso2d',
    'zoomIn2d',
    'zoomOut2d',
    'autoScale2d',
    'toggleSpikelines',
  ],
};

const UNIFIED_HOVER_LABEL_OFFSET = 15;
const HOVER_BOX_GAP_PX = 14;
const HOVER_BOX_MIN_WIDTH_PX = 180;
const HOVER_BOX_MAX_WIDTH_PX = 280;
const HOVER_DISTANCE_PX = 10;

// Plotly does not expose a layout setting for the unified hover label's gap
// from the hovered point, so nudge the generated SVG group after it is drawn.
// Remember the shifted transform to avoid accumulating the offset when Plotly
// and React both update the same hover state.
export const shiftUnifiedHoverLabel = (
  container,
  offset = UNIFIED_HOVER_LABEL_OFFSET
) => {
  const label = container && container.querySelector('.hoverlayer .legend');
  if (!label) return;

  const transform = label.getAttribute('transform') || '';
  if (label.getAttribute('data-shifted-transform') === transform) return;

  const match = transform.match(
    /^translate\(\s*(-?[\d.]+)[, ]+\s*(-?[\d.]+)\s*\)$/
  );
  if (!match) return;

  const verticalSpike = Array.from(
    container.querySelectorAll('.hoverlayer line.spikeline')
  ).find((line) => {
    const x1 = Number(line.getAttribute('x1'));
    const x2 = Number(line.getAttribute('x2'));
    return Number.isFinite(x1) && x1 === x2;
  });
  const labelX = Number(match[1]);
  const spikeX = verticalSpike
    ? Number(verticalSpike.getAttribute('x1'))
    : null;
  const direction = spikeX !== null && labelX < spikeX ? -1 : 1;
  const shiftedTransform = `translate(${labelX + direction * offset},${
    match[2]
  })`;
  label.setAttribute('transform', shiftedTransform);
  label.setAttribute('data-shifted-transform', shiftedTransform);
};

// Plotly can reposition the unified hover label after the hover event has
// finished (for example, after React updates the running-dot shapes). Keep the
// requested gap applied for as long as the label remains visible.
export const observeUnifiedHoverLabel = (container) => {
  if (!container || typeof MutationObserver === 'undefined') return () => {};

  const observer = new MutationObserver(() =>
    shiftUnifiedHoverLabel(container)
  );
  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['transform'],
  });

  return () => observer.disconnect();
};

const formatHoverTitle = (x) => {
  if (x instanceof Date && !isNaN(x.getTime())) {
    const iso = x.toISOString();
    return `${iso.slice(0, 10)}, ${iso.slice(11, 23)}`;
  }
  if (typeof x === 'string') {
    const normalized = x.replace('T', ' ').replace('Z', '');
    const [datePart, timePart] = normalized.split(' ');
    if (datePart && timePart) return `${datePart}, ${timePart}`;
  }
  return String(x ?? '');
};

const isTransientLabel = (label) => String(label || '').includes('(transient)');
const transientBaseLabel = (label) =>
  String(label || '').replace(/\s+\(transient\)$/, '');

const dedupeHoverItems = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = item.dedupeKey
      || (isTransientLabel(item.label)
        ? `transient:${transientBaseLabel(item.label)}`
        : `${item.label}|${item.value}|${item.color}`);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const TrendOverlayGraph = ({
  sensorSeries,
  timeZone,
  projectTimezone,
  transientThresholds,
  showWaveforms,
  visibleRange,
  axisSettings,
  pressureUnit,
  onZoomChange,
  onOpenContextMenu,
  canBack,
  canForward,
  onBack,
  onForward,
  onReset,
  t,
}) => {
  const conversion =
    PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;
  const [hoverDots, setHoverDots] = React.useState(null);
  const [hoverBox, setHoverBox] = React.useState(null);
  const [hoverDebugItems, setHoverDebugItems] = React.useState(null);
  const plotContainerRef = React.useRef(null);

  React.useEffect(() => observeUnifiedHoverLabel(plotContainerRef.current), []);

  const traces = React.useMemo(
    () =>
      buildOverlayTraces({
        sensorSeries,
        timeZone,
        projectTimezone,
        transientThresholds,
        showWaveforms,
      }).map((trace) => ({
        ...trace,
        y: trace.y.map((v) =>
          v == null || isNaN(v) ? v : Number((v * conversion.factor).toFixed(2))
        ),
      })),
    [
      sensorSeries,
      timeZone,
      projectTimezone,
      transientThresholds,
      showWaveforms,
      conversion.factor,
    ]
  );

  // Rangeslider always shows the full loaded extent, regardless of zoom.
  // Trace x values are timezone-naive ISO strings (see toPlotTime); keep the
  // range in the same string form — Date objects would be re-anchored to the
  // browser timezone by Plotly and drift off the data.
  const fullRange = React.useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    traces.forEach((trace) =>
      (trace.x || []).forEach((d) => {
        const ms = Date.parse(d);
        if (isNaN(ms)) return;
        if (ms < min) min = ms;
        if (ms > max) max = ms;
      })
    );
    return min < max
      ? [new Date(min).toISOString(), new Date(max).toISOString()]
      : undefined;
  }, [traces]);

  // RADAR-style "running dots": mark each line's value at the hovered x.
  // Drawn as pixel-sized layout shapes rather than a marker trace — trace
  // markers join Plotly's autorange (their pixel padding widens the axis),
  // which made the default (autorange) view jump when hovering edge points.
  const hoverShapes = React.useMemo(
    () =>
      (hoverDots || []).map((d) => ({
        type: 'circle',
        xref: 'x',
        yref: 'y',
        xsizemode: 'pixel',
        ysizemode: 'pixel',
        xanchor: d.x,
        yanchor: d.y,
        x0: -4.5,
        x1: 4.5,
        y0: -4.5,
        y1: 4.5,
        fillcolor: d.color,
        line: { color: '#fff', width: 1 },
        layer: 'above',
      })),
    [hoverDots]
  );

  const layout = {
    autosize: true,
    showlegend: true,
    legend: {
      orientation: 'h',
      xanchor: 'center',
      x: 0.5,
      yanchor: 'top',
      y: -0.4,
      font: { size: 11 },
    },
    margin: { t: 10, b: 10, l: 55, r: 20 },
    hovermode: 'x unified',
    hoverdistance: HOVER_DISTANCE_PX,
    spikedistance: HOVER_DISTANCE_PX,
    hoverlabel: {
      bgcolor: 'rgba(255,255,255,0.96)',
      bordercolor: '#d8e2ea',
      font: {
        size: 12,
        color: '#1d2734',
      },
      grouptitlefont: {
        size: 12,
        color: '#07042e',
      },
    },
    dragmode: 'zoom',
    uirevision: 'trend-overlay',
    xaxis: {
      type: 'date',
      showspikes: true,
      spikecolor: '#07042e',
      spikedash: 'solid',
      spikethickness: 1,
      spikemode: 'across',
      hoverformat: '%Y-%m-%d %H:%M:%S.%L',
      tickformatstops: TICKFORMAT_STOPS,
      rangeslider: {
        visible: true,
        thickness: 0.1,
        range: fullRange,
        bgcolor: '#f8fbfd',
        bordercolor: '#d8e2ea',
        borderwidth: 1,
      },
      range: visibleRange
        ? [
          toPlotTime(visibleRange.from, timeZone, projectTimezone),
          toPlotTime(visibleRange.to, timeZone, projectTimezone),
        ]
        : undefined,
      autorange: !visibleRange,
    },
    yaxis:
      axisSettings.auto !== false
        ? {
            autorange: true,
            rangemode: 'normal',
            title: { text: conversion.label },
          }
        : {
            range: [axisSettings.min, axisSettings.max],
            autorange: false,
            title: { text: conversion.label },
          },
    shapes: hoverShapes,
  };

  const handleRelayout = (event) => {
    const range = relayoutToRange(event, timeZone, projectTimezone);
    if (range !== undefined) onZoomChange(range);
  };

  const handleHover = (event) => {
    const container = plotContainerRef.current;
    const allHoverItems = (event.points || [])
      .filter((p) => p.data && p.data.mode === 'lines')
      .map((p) => ({
        x: p.x,
        y: p.y,
        label: String(p.data?.name || ''),
        color:
          (p.fullData && p.fullData.line && p.fullData.line.color) || '#333',
      }));
    setHoverDebugItems(
      (event.points || []).map((p, index) => ({
        index,
        label: String(p.data?.name || ''),
        x: p.x,
        y: p.y,
        curveNumber: p.curveNumber,
        pointNumber: p.pointNumber,
        color: (p.fullData && p.fullData.line && p.fullData.line.color) || '#333',
      }))
    );
    const dotPoints = allHoverItems.filter(
      (p) => !isTransientLabel(p.label)
    );
    if (!allHoverItems.length) return;
    setHoverDots(dotPoints);
    const spike = container?.querySelector('.hoverlayer line.spikeline');
    const plotWidth = container?.getBoundingClientRect().width || 0;
    const spikeX = spike ? Number(spike.getAttribute('x1')) : null;
    const side = spikeX != null && plotWidth
      ? (spikeX < plotWidth / 2 ? 'right' : 'left')
      : 'right';
    setHoverBox({
      title: formatHoverTitle(allHoverItems[0].x),
      side,
      spikeX,
      plotWidth,
      items: dedupeHoverItems(allHoverItems.map((p) => ({
        label: p.label,
        value: Number.isFinite(Number(p.y)) ? Number(p.y).toFixed(2) : String(p.y),
        color: p.color,
        dedupeKey: isTransientLabel(p.label)
          ? `transient:${p.label}:${Number.isFinite(Number(p.y)) ? Number(p.y).toFixed(2) : String(p.y)}`
          : `line:${p.label}:${Number.isFinite(Number(p.y)) ? Number(p.y).toFixed(2) : String(p.y)}:${p.color}`,
      }))),
    });
    shiftUnifiedHoverLabel(plotContainerRef.current);
  };

  const handleUnhover = () => {
    setHoverDots(null);
    setHoverBox(null);
    setHoverDebugItems(null);
  };

  const hoverBoxPosition = React.useMemo(() => {
    if (!hoverBox) return null;

    const fallback = hoverBox.side === 'left'
      ? { right: `${HOVER_BOX_GAP_PX}px` }
      : { left: `${HOVER_BOX_GAP_PX}px` };

    if (!Number.isFinite(hoverBox.spikeX) || !hoverBox.plotWidth) return fallback;

    if (hoverBox.side === 'left') {
      const right = Math.max(
        HOVER_BOX_GAP_PX,
        hoverBox.plotWidth - hoverBox.spikeX + HOVER_BOX_GAP_PX
      );
      return { right: `${right}px` };
    }

    const left = Math.min(
      Math.max(HOVER_BOX_GAP_PX, hoverBox.spikeX + HOVER_BOX_GAP_PX),
      hoverBox.plotWidth - HOVER_BOX_MIN_WIDTH_PX - HOVER_BOX_GAP_PX
    );
    return { left: `${left}px` };
  }, [hoverBox]);

  return (
    <div
      ref={plotContainerRef}
      className='trend-overlay-plot'
      style={{
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onOpenContextMenu({ x: e.clientX, y: e.clientY });
      }}
    >
      <Plot
        data={traces}
        layout={layout}
        config={CONFIG}
        useResizeHandler
        style={{ width: '100%', flex: '1 1 auto', minHeight: 0 }}
        onRelayout={handleRelayout}
        onHover={handleHover}
        onUnhover={handleUnhover}
        onUpdate={() => shiftUnifiedHoverLabel(plotContainerRef.current)}
      />
      {hoverBox && (
        <div
          style={{
            position: 'absolute',
            top: '18px',
            zIndex: 3,
            pointerEvents: 'none',
            minWidth: `${HOVER_BOX_MIN_WIDTH_PX}px`,
            maxWidth: `${HOVER_BOX_MAX_WIDTH_PX}px`,
            background: 'rgba(255,255,255,0.96)',
            border: '1px solid #d8e2ea',
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(7, 4, 46, 0.12)',
            padding: '8px 10px',
            color: '#1d2734',
            fontSize: '12px',
            lineHeight: 1.35,
            ...hoverBoxPosition,
          }}
        >
          <div
            style={{
              marginBottom: '6px',
              fontWeight: 700,
              color: '#07042e',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {hoverBox.title}
          </div>
          {hoverBox.items.map((item, index) => (
            <div
              key={item.dedupeKey || `${item.label}|${item.value}|${item.color}|${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                marginTop: '3px',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    flex: '0 0 auto',
                  }}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </span>
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
      {hoverDebugItems?.length ? (
        <pre
          style={{
            position: 'absolute',
            left: '12px',
            bottom: '44px',
            zIndex: 3,
            maxWidth: '360px',
            maxHeight: '180px',
            overflow: 'auto',
            margin: 0,
            padding: '8px 10px',
            background: 'rgba(7, 4, 46, 0.88)',
            color: '#f7f9fc',
            borderRadius: '6px',
            fontSize: '11px',
            lineHeight: 1.35,
            pointerEvents: 'none',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {`lines: ${hoverDebugItems.filter((item) => !isTransientLabel(item.label)).length}\ntransients: ${hoverDebugItems.filter((item) => isTransientLabel(item.label)).length}`}
        </pre>
      ) : null}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '2px 8px',
          flexShrink: 0,
        }}
      >
        <IconButton
          size='small'
          disabled={!canBack}
          onClick={onBack}
          aria-label='zoom back'
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            width: 28,
            height: 28,
          }}
        >
          <ChevronLeftIcon fontSize='small' />
        </IconButton>
        <IconButton
          size='small'
          disabled={!canForward}
          onClick={onForward}
          aria-label='zoom forward'
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            width: 28,
            height: 28,
          }}
        >
          <ChevronRightIcon fontSize='small' />
        </IconButton>
        <Button size='small' variant='outlined' onClick={onReset}>
          {t('reset_zoom')}
        </Button>
      </div>
    </div>
  );
};

TrendOverlayGraph.propTypes = {
  sensorSeries: PropTypes.array.isRequired,
  timeZone: PropTypes.string,
  projectTimezone: PropTypes.string,
  transientThresholds: PropTypes.object,
  showWaveforms: PropTypes.bool,
  visibleRange: PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
  }),
  axisSettings: PropTypes.object.isRequired,
  pressureUnit: PropTypes.string,
  onZoomChange: PropTypes.func.isRequired,
  onOpenContextMenu: PropTypes.func.isRequired,
  canBack: PropTypes.bool,
  canForward: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onForward: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default connect((state) => ({
  pressureUnit: state.local.pressureUnit || 'meter',
}))(TrendOverlayGraph);
